import { useState, useEffect } from "react";
import { Button, HStack, Popover, Portal, Separator, Text, Textarea, VStack, Box } from "@chakra-ui/react";
import { LuArrowBigLeft, LuArrowBigRight, LuArrowLeftRight, LuRepeat, LuSendToBack } from "react-icons/lu";

import { useAppStore } from "../../../context/store";
import { Tooltip } from "../../ui/tooltip";
import { clearHighlightForResult, findLaneletEntitiesForId, highlightSearchResult, parseIdsFromText, reverseLaneletGeometries, switchLeftRightLines } from "../../../utilies/lanelet/CheckerByIds";
import { matchKeyInText, removeFileExtension, showToast } from "../../../utilies/Utility";
import Type from "../../../utilies/Types";
import { useMapObjects } from "../../../context/MapObjectsContext";

function LaneletCheckByIds() {
  const viewerRef = useAppStore((s) => s.viewerRef);
  const editMode = useAppStore((s) => s.editMode);
  const userData = useAppStore((s) => s.userData);
  const { mapObjects } = useMapObjects();
  const highlightToolsRef = useAppStore((s) => s.highlightToolsRef);
  const infoboxRef = useAppStore((s) => s.infoboxRef);

  const activeLayer = useAppStore((s) => s.activeLayer);

  const checkLaneletMode = useAppStore((s) => s.checkLaneletMode);
  const setCheckLaneletMode = useAppStore((s) => s.setCheckLaneletMode);

  const [textareaValue, setTextareaValue] = useState("");
  const [ids, setIds] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [currentResult, setCurrentResult] = useState(null);

  const [isOpen, setIsOpen] = useState(false);

  const [showCheckLaneletButton, setShowCheckLaneletButton] = useState([]);

  const hasIds = ids.length > 0 && currentIndex >= 0 && currentIndex < ids.length;
  const currentId = hasIds ? ids[currentIndex] : null;

  const [mainDataSources, setMainDataSources] = useState([]);
  const [sideDataSources, setSideDataSources] = useState([]);

  const appendLog = (line, type = Type.NOTIFICATION.SUCCESS) => {
    if (typeof showToast === "function") {
      showToast(line, type, { closable: true });
    }
  };

  const buildDataSources = () => {
    let main = [];
    let side = [];

    for (const key in mapObjects) {
      const obj = mapObjects[key];
      if (!obj) continue;

      const fileName = obj.file_name || "";
      const ds = obj.dataSource;
      if (!ds) continue;

      if (matchKeyInText(fileName, "lanelet")) {
        main.push(ds);
      }

      if (matchKeyInText(fileName, "lanemarker") || matchKeyInText(fileName, "virtualline") || matchKeyInText(fileName, "turningline")) {
        side.push(ds);
      }
    }

    setMainDataSources(main);
    setSideDataSources(side);

    return { main, side };
  };

  const handleStart = () => {
    const { main, side } = buildDataSources();

    const parsed = parseIdsFromText(textareaValue);
    if (parsed.length === 0) {
      setIds([]);
      setCurrentIndex(-1);
      setCurrentResult(null);
      setCheckLaneletMode(false);
      appendLog("No valid ids in input", Type.NOTIFICATION.ERROR);
      return;
    }

    clearHighlightForResult(currentResult, highlightToolsRef);

    setIds(parsed);
    setCurrentIndex(0);
    setCheckLaneletMode(true);

    const id = parsed[0];
    const result = findLaneletEntitiesForId(id, main, side);
    setCurrentResult(result);

    highlightSearchResult(result, highlightToolsRef, viewerRef);

    if (result.mainEntity && viewerRef && viewerRef.current) {
      infoboxRef.current?.openForEntity(result.mainEntity);
    }

    const msgBase = "ID " + id;
    if (result.messages.length === 0) {
      appendLog(msgBase + ": all entities found", Type.NOTIFICATION.SUCCESS);
    } else {
      result.messages.forEach((m) => appendLog(m, Type.NOTIFICATION.ERROR));
    }
  };

  const handleGoToIndex = (nextIndex) => {
    if (!ids.length) return;
    if (nextIndex < 0 || nextIndex >= ids.length) return;
    if (!mainDataSources.length && !sideDataSources.length) return;

    clearHighlightForResult(currentResult, highlightToolsRef);

    const id = ids[nextIndex];
    const result = findLaneletEntitiesForId(id, mainDataSources, sideDataSources);

    setCurrentIndex(nextIndex);
    setCurrentResult(result);

    highlightSearchResult(result, highlightToolsRef, viewerRef);

    if (result.mainEntity && viewerRef && viewerRef.current) {
      infoboxRef.current?.openForEntity(result.mainEntity);
    }

    const msgBase = "ID " + id;
    if (result.messages.length === 0) {
      appendLog(msgBase + ": all entities found", Type.NOTIFICATION.SUCCESS);
    } else {
      result.messages.forEach((m) => appendLog(m, Type.NOTIFICATION.ERROR));
    }
  };

  const handleNext = () => {
    if (!ids.length) return;
    handleGoToIndex(currentIndex + 1);
  };

  const handlePrevious = () => {
    if (!ids.length) return;
    handleGoToIndex(currentIndex - 1);
  };

  const handleReset = () => {
    clearHighlightForResult(currentResult, highlightToolsRef);
    setIds([]);
    setCurrentIndex(-1);
    setCurrentResult(null);
    // setCheckLaneletMode(false);
    appendLog("Checker reset", Type.NOTIFICATION.INFO);
  };

  const handleSwitch = () => {
    if (!currentResult) return;
    switchLeftRightLines(currentResult, userData);
    appendLog("ID " + currentResult.id + ": switch applied on left/right entities", Type.NOTIFICATION.SUCCESS);
    highlightSearchResult(currentResult, highlightToolsRef, viewerRef);
    if (currentResult.mainEntity && viewerRef && viewerRef.current) {
      infoboxRef.current?.openForEntity(currentResult.mainEntity);
    }
  };

  const handleReverse = () => {
    if (!currentResult) return;
    reverseLaneletGeometries(currentResult, userData);
    appendLog("ID " + currentResult.id + ": reverse applied on main/left/right geometries", Type.NOTIFICATION.SUCCESS);
    if (viewerRef && viewerRef.current && viewerRef.current.refresh) {
      viewerRef.current.refresh();
    }
  };

  useEffect(() => {
    let isShow = false;

    if (activeLayer != null) {
      for (const key in mapObjects) {
        if (mapObjects[key].id == activeLayer) {
          const element = mapObjects[key];
          if (removeFileExtension(element.file_name).toLowerCase().includes("lanelet")) {
            if (element.visible) {
              isShow = true;
              break;
            }
          }
        }
      }
    }

    setShowCheckLaneletButton(isShow);
    if (!isShow) {
      setIsOpen(false);
      setCheckLaneletMode(false);
    }
  }, [activeLayer, mapObjects, setCheckLaneletMode]);

  useEffect(() => {
    if (!checkLaneletMode && isOpen && ids.length === 0) {
      setIsOpen(false);
    }
  }, [checkLaneletMode, isOpen, ids.length]);

  if (!showCheckLaneletButton) return null;

  const isActive = ids.length > 0 && currentIndex >= 0;

  return (
    <HStack>
      <Popover.Root
        positioning={{ placement: "bottom" }}
        closeOnInteractOutside={false}
        closeOnEscape={false}
        open={isOpen}
        onOpenChange={(details) => {
          setIsOpen(!!details?.open);
        }}
      >
        <Popover.Trigger asChild>
          <Button
            disabled={editMode}
            userSelect="none"
            p={0}
            opacity={1}
            bg={isActive ? "green" : "white"}
            color={isActive ? "white" : "MenuText"}
            colorPalette={isActive ? "green" : "white"}
            onClick={() => {
              setIsOpen((prev) => !prev);
            }}
          >
            <Tooltip content="Check Lanelets By IDs">
              <LuRepeat size={20} />
            </Tooltip>
          </Button>
        </Popover.Trigger>

        <Portal>
          <Popover.Positioner
            style={{
              position: "fixed",
              left: "50%",
              top: "auto",
              bottom: "24px",
              transform: "translateX(-50%)",
              zIndex: 1000,
            }}
          >
            <Popover.Content maxW="480px" width="90vw" position="relative">
              <Button
                onClick={() => {
                  setIsOpen(false);
                }}
                size="xs"
                variant="ghost"
                position="absolute"
                top="4px"
                right="4px"
                borderRadius="full"
              >
                ✕
              </Button>

              <Popover.Body p={2} pt={6}>
                <VStack align="stretch" gap={2}>
                  <Text>Enter IDs</Text>
                  <Textarea rows={5} placeholder="ids ... " height="100px" value={textareaValue} onChange={(e) => setTextareaValue(e.target.value)} />

                  <Box fontSize="sm">
                    <Text>
                      Current ID:{" "}
                      <Box as="span" fontWeight="bold">
                        {currentId || "-"}
                      </Box>
                    </Text>
                  </Box>

                  <HStack gap={1} justify="space-between">
                    <HStack gap={1}>
                      <Button onClick={isActive ? handleReset : handleStart} colorPalette={isActive ? "red" : undefined}>
                        {isActive ? "Clear" : "Start"}
                      </Button>
                    </HStack>

                    <Separator orientation="vertical" height="10" mx={2} />

                    <HStack gap={1}>
                      <Button colorPalette="MenuText" p={0} borderRadius="full" onClick={handleSwitch} aria-label="Switch">
                        <Tooltip content="Switch">
                          <LuArrowLeftRight />
                        </Tooltip>
                      </Button>
                      <Button colorPalette="MenuText" p={0} borderRadius="full" onClick={handleReverse} aria-label="Reverse">
                        <Tooltip content="Reverse">
                          <LuSendToBack />
                        </Tooltip>
                      </Button>
                    </HStack>

                    <Separator orientation="vertical" height="10" mx={2} />

                    <HStack gap={1}>
                      <Button colorPalette="MenuText" p={0} borderRadius="full" onClick={handlePrevious} aria-label="Previous" disabled={!ids.length || currentIndex <= 0}>
                        <Tooltip content="Previous">
                          <LuArrowBigLeft />
                        </Tooltip>
                      </Button>
                      <Button colorPalette="MenuText" p={0} borderRadius="full" onClick={handleNext} aria-label="Next" disabled={!ids.length || currentIndex >= ids.length - 1}>
                        <Tooltip content="Next">
                          <LuArrowBigRight />
                        </Tooltip>
                      </Button>
                    </HStack>
                  </HStack>
                </VStack>
              </Popover.Body>
            </Popover.Content>
          </Popover.Positioner>
        </Portal>
      </Popover.Root>
    </HStack>
  );
}

export default LaneletCheckByIds;
